// The draft as the review screen opens it (stage 3, #331 B1).
//
// The review form does not show the stored row as is. Opening a draft rounds
// its line money, masks deposit and purchase tax by the supplier's flags, lays
// the catalog match on the lines, picks the VAT mode and recomputes subtotal,
// VAT and total from the lines. So a draft stored as 200 / 36 / 236 over lines
// summing to 100 opens as 100 / 18 / 118 — and the stage-3 gate must judge
// that, not the row (spec 2026-10-25-stage3-gate-design.md, "What the gate
// judges"). These steps used to be spread over DocumentViewPage's
// initEditForm and two effects that ran after it; they live here now, as one
// pure function the page calls on every opening (cold, warm prefetch, the
// re-check's reopen, עריכה after a save) and the server runs too.
//
// MUST STAY IN SYNC with server/lib/openingForm.js. Both suites run the same
// fixtures (server/lib/openingForm.fixtures.json), which pin the outputs and
// the formRev strings byte for byte.
//
// `context` is what the page fetches, passed in so the function stays pure:
//   supplier        { has_deposit, has_purchase_tax, invoice_rules,
//                     fixed_invoice_discount, fixed_invoice_discount_type,
//                     uses_delivery_notes } — the suppliers row's own column
//                     names; null for a draft with no supplier
//   vatRate         the restaurant's VAT rate, in percent
//   catalog         the pricesOnly catalog match, one row per opening line;
//                   null/undefined when it was skipped or failed
//   openNotesCount  the supplier's open delivery notes (null when unknown)
import { round2 } from './money';
import { lineMoney2dp, roundDocTotal } from './lineCalc';
import { detectVatRule } from './vatRule';
import { initialVatMode } from './editVatMode';

// Invoice-level discount: 'percent' → subtotal × (d/100); 'amount' → d as-is.
export function calcInvoiceDiscountAmount(subtotal, discount, discountType) {
  const s = parseFloat(subtotal) || 0;
  const d = parseFloat(discount) || 0;
  return discountType === 'amount' ? d : s * (d / 100);
}

// The lines the form opens with, before the catalog match — also what the
// match is asked about (the prefetch and the re-check send these rows).
// Suppliers without the flags must not contribute deposit/purchase_tax to
// line_total — defensive zero-out so a stale value saved earlier (or a
// supplier switch later) can't double-count. lineMoney2dp: a legacy 3-decimal
// line total opens already rounded, so the recalculated subtotal is the one
// the save stores (#274). `_key` is the line's index in the document.
export function openingItems(items, supplier) {
  const hasDeposit     = !!supplier?.has_deposit;
  const hasPurchaseTax = !!supplier?.has_purchase_tax;
  return (items || []).map((it, i) => lineMoney2dp({
    ...it,
    discount: it.discount ?? 0, discount_type: it.discount_type ?? 'percent',
    credit_qty: it.credit_qty ?? 0, credit_price_diff: it.credit_price_diff ?? 0, credit_total: it.credit_total ?? 0,
    deposit:      hasDeposit     ? (it.deposit ?? 0)      : 0,
    purchase_tax: hasPurchaseTax ? (it.purchase_tax ?? 0) : 0,
    _key: i,
  }));
}

// catalog_last_price / catalog_fixed_price on each row, so the edit table can
// render the same price-change indicator that the upload page does.
export function withCatalogPrices(baseItems, enriched) {
  return baseItems.map((it, i) => ({
    ...it,
    catalog_last_price:        enriched[i]?.catalog_last_price,
    catalog_fixed_price:       enriched[i]?.catalog_fixed_price,
    catalog_fixed_discount:    enriched[i]?.catalog_fixed_discount    || 0,
    catalog_fixed_discount_type: enriched[i]?.catalog_fixed_discount_type || 'percent',
  }));
}

// Subtotal from the lines, or the document's own net when it has no lines: a
// printed net with nothing to sum must not open as 0 and be saved so (#385). The
// page clears it when the reviewer removes every line of a document that had some.
// A document stored with a total and no net at all (a bulk upload's קבלה) keeps
// that total the same way: there is nothing to work it out from.
// VAT (by mode and rule) applies to
// taxable = subtotal − invoice-level discount. The total adds the VAT ROUNDED
// to the agora — a hand-typed 27.006 is stored as 27.01 and the server
// derives the total from that (server/lib/moneyPrecision.js), so adding the
// raw 27.006 showed 88.03 for a saved 88.04 (#274). The page runs this on
// every line edit too, so the opening and the open form use one formula.
export function formTotals(form, items, { vatMode, vatRate, vatRule }) {
  const subtotal = items.length === 0
    ? round2(parseFloat(form.subtotal) || 0)
    : round2(items.reduce((s, it) => s + (parseFloat(it.line_total) || 0), 0));
  const taxable = subtotal - calcInvoiceDiscountAmount(subtotal, form.invoice_discount, form.invoice_discount_type);
  let vat;
  if (vatRule.vatInclusive) {
    // Prices already include VAT — no separate VAT line.
    vat = 0;
  } else if (vatRule.partialVat) {
    // Partial VAT: preserve the saved/printed value (manual-like) instead of
    // auto-computing 18% — produce invoices carry variable, item-mix VAT.
    vat = parseFloat(form.vat) || 0;
  } else if (vatMode === 'auto') {
    vat = round2(taxable * (vatRate / 100));
  } else {
    vat = parseFloat(form.vat) || 0;
  }
  if (items.length === 0 && subtotal === 0 && parseFloat(form.total)) {
    return { subtotal, vat, total: round2(parseFloat(form.total)) };
  }
  return { subtotal, vat, total: roundDocTotal(taxable + round2(vat)) };
}

// The stored document fields the review checks read beside the form. Writers
// change them without touching the form: an alias edit rewrites
// recipient_match (server/lib/restaurantAliases.js), deleting or restoring a
// twin flips duplicate_warning (server/lib/softDelete.js), the OCR supplier
// re-read sets ocr_matched_supplier_id, a price backfill raises
// vat_needs_check. They are part of formRev, so a screen still showing the
// old warning never binds to the new one (Codex, #341).
export const STORED_TEXT = ['recipient_match', 'extracted_recipient_name', 'extracted_recipient_business_id', 'printed_invoice_discount_type'];
export const STORED_NUM  = [
  'duplicate_warning', 'vat_needs_check', 'ocr_matched_supplier_id',
  'printed_subtotal', 'printed_invoice_discount', 'printed_vat', 'printed_total',
];

// → { form, items, vatMode, openedVatMode, stored, checks }
//   form           the header as the form shows it, totals recomputed
//   items          the opening lines, catalog prices laid on
//   vatMode        the mode the form settles in: a supplier VAT rule forces
//                  'manual' (the page's rule effect does the same on a
//                  supplier change)
//   openedVatMode  the mode chosen from the STORED header (initialVatMode),
//                  before the rule — what דלג compares a toggle against, and
//                  what the "opened in manual" hint explains
//   stored         the STORED_TEXT / STORED_NUM fields as the row has them
//   checks         the opening inputs the review checks read, for the gate;
//                  the page derives its own live from the selected supplier
export function openingForm(doc, items, context = {}) {
  const { supplier = null, vatRate, catalog = null, openNotesCount = null } = context;
  let lines = openingItems(items, supplier);
  if (catalog) lines = withCatalogPrices(lines, catalog);
  const header = {
    supplier_id: doc.supplier_id, doc_type: doc.doc_type,
    doc_num: doc.doc_num || '', date: doc.date || '',
    status: doc.status, subtotal: doc.subtotal, vat: doc.vat, total: doc.total,
    invoice_discount: doc.invoice_discount ?? 0,
    invoice_discount_type: doc.invoice_discount_type || 'percent',
    is_price_diff_credit: doc.is_price_diff_credit ? 1 : 0,
  };
  // The VAT mode is decided from the STORED header, which is what keeps an
  // untouched auto-VAT document in auto (utils/editVatMode.js).
  const openedVatMode = initialVatMode(doc, vatRate);
  const vatRule = detectVatRule(supplier?.invoice_rules, doc.doc_type);
  const vatMode = vatRule.vatInclusive || vatRule.partialVat ? 'manual' : openedVatMode;
  const form = { ...header, ...formTotals(header, lines, { vatMode, vatRate, vatRule }) };
  const stored = {};
  for (const k of [...STORED_TEXT, ...STORED_NUM]) stored[k] = doc[k] ?? null;
  const checks = {
    vatRate: parseFloat(vatRate) || 0,
    vatRule: { inclusive: vatRule.vatInclusive, partial: vatRule.partialVat },
    agreedInvoiceDiscount: supplier?.fixed_invoice_discount ?? null,
    agreedInvoiceDiscountType: supplier?.fixed_invoice_discount_type || 'percent',
    supplierUsesDeliveryNotes: supplier && supplier.uses_delivery_notes != null ? !!supplier.uses_delivery_notes : null,
    openNotesCount: openNotesCount ?? null,
  };
  return { form, items: lines, vatMode, openedVatMode, stored, checks };
}

// ── formRev: the name of the form on screen ──
//
// A small deterministic hash of an opening form, so a gate view (B2,
// POST /:id/gate-view) can say which form the reviewer was shown and the
// server can tell whether its own rebuild is that same form. It covers what
// the reviewer sees and edits — the header, the lines, the VAT mode — plus the
// stored fields the checks read (`stored`) — and not `checks`: the client
// learns the open-notes count only after the form is up, and the server
// re-reads the supplier's terms itself.
//
// Canonical JSON over a FIXED projection rather than the objects as they
// come: GET /:id decorates its lines (no_invoice_match), the page adds
// `_key`, and a missing catalog row leaves a key undefined on one side and
// absent on the other. Every projected key is always present; numbers are
// normalised (numeric strings read as numbers, -0 as 0, 15 significant
// digits, junk and blank or whitespace-only strings as null); text is a
// string. Keys sorted.
const REV_HEADER_TEXT = ['doc_type', 'doc_num', 'date', 'status', 'invoice_discount_type'];
const REV_HEADER_NUM  = ['supplier_id', 'subtotal', 'vat', 'total', 'invoice_discount', 'is_price_diff_credit'];
const REV_LINE_TEXT = ['sku', 'description', 'discount_type', 'line_kind', 'adjustment_type', 'catalog_fixed_discount_type'];
const REV_LINE_NUM  = [
  'id', 'qty', 'unit_price', 'discount', 'line_total', 'credit_qty', 'credit_price_diff', 'credit_total',
  'deposit', 'purchase_tax', 'price_estimated', 'catalog_last_price', 'catalog_fixed_price', 'catalog_fixed_discount',
];
// Bumped when the projection or the hash changes: a rev of one shape never
// equals one of another.
const FORM_REV_VERSION = 'f1';

const revText = (v) => (v === null || v === undefined ? '' : String(v));
function revNum(v) {
  if (v === null || v === undefined) return null;
  if (typeof v === 'string' && v.trim() === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return null;
  return Number(n.toPrecision(15)) || 0;
}
function project(obj, textKeys, numKeys) {
  const out = {};
  for (const k of textKeys) out[k] = revText(obj?.[k]);
  for (const k of numKeys) out[k] = revNum(obj?.[k]);
  return out;
}
function canonicalJson(v) {
  if (Array.isArray(v)) return `[${v.map(canonicalJson).join(',')}]`;
  if (v && typeof v === 'object') {
    return `{${Object.keys(v).sort().map(k => `${JSON.stringify(k)}:${canonicalJson(v[k])}`).join(',')}}`;
  }
  return JSON.stringify(v);
}
// cyrb53 (public domain): 53 bits from two 32-bit lanes over the UTF-16 code
// units — the same on any JS engine, Hebrew included.
function cyrb53(str) {
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
  h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return 4294967296 * (2097151 & h2) + (h1 >>> 0);
}

// The canonical text formRev hashes — exported for the fixtures' diagnostics.
export function formRevText(opening) {
  return canonicalJson({
    form: project(opening?.form, REV_HEADER_TEXT, REV_HEADER_NUM),
    items: (opening?.items || []).map(it => project(it, REV_LINE_TEXT, REV_LINE_NUM)),
    vatMode: revText(opening?.vatMode),
    stored: project(opening?.stored, STORED_TEXT, STORED_NUM),
  });
}

// `opening`: openingForm's result, or the same shape from the form on screen.
export function formRev(opening) {
  return `${FORM_REV_VERSION}-${cyrb53(formRevText(opening)).toString(16).padStart(14, '0')}`;
}
